import React, { useState, useEffect, useRef, useCallback } from "react";
import Button from "src/components/Button/Button";
import "./FastCatTimer.scss";

interface IProps {
  onTimeUpdate: (time: string) => void;
  initialTime?: string;
}

const FastCatTimer: React.FC<IProps> = ({ onTimeUpdate, initialTime }) => {
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [elapsedTime, setElapsedTime] = useState<number>(0); // in milliseconds
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // Parse initial time if provided (format: "SS.MM" -> milliseconds)
  useEffect(() => {
    if (initialTime && initialTime !== "") {
      const timeValue = parseFloat(initialTime);
      if (!isNaN(timeValue)) {
        setElapsedTime(timeValue * 1000); // Convert seconds to milliseconds
      }
    }
  }, [initialTime]);

  // Format milliseconds to "SS.MM" format for Fast CAT
  const formatTime = useCallback((ms: number): string => {
    const totalSeconds = ms / 1000;
    const seconds = Math.floor(totalSeconds);
    const milliseconds = Math.floor((totalSeconds - seconds) * 100); // Get hundredths
    
    return `${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
  }, []);

  // Format milliseconds for display (SS.MMM with thousandths)
  const formatDisplayTime = useCallback((ms: number): string => {
    const totalSeconds = ms / 1000;
    const seconds = Math.floor(totalSeconds);
    const milliseconds = Math.floor((totalSeconds - seconds) * 1000); // Get thousandths for display
    
    return `${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  }, []);

  const startTimer = useCallback(() => {
    if (!isRunning) {
      setIsRunning(true);
      startTimeRef.current = Date.now() - elapsedTime;
      
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const newElapsedTime = now - startTimeRef.current;
        setElapsedTime(newElapsedTime);
      }, 10); // Update every 10ms for smooth display
    }
  }, [isRunning, elapsedTime]);

  const stopTimer = useCallback(() => {
    if (isRunning && intervalRef.current) {
      setIsRunning(false);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      
      // Update parent with the final time in Fast CAT format (SS.MM)
      const finalTime = formatTime(elapsedTime);
      onTimeUpdate(finalTime);
    }
  }, [isRunning, elapsedTime, formatTime, onTimeUpdate]);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsRunning(false);
    setElapsedTime(0);
    onTimeUpdate("");
  }, [onTimeUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className="FastCatTimer">
      <div className="timer-display">
        <div className="time-value">
          {formatDisplayTime(elapsedTime)}
        </div>
        <div className="time-label">
          seconds
        </div>
      </div>
      
      <div className="timer-controls">
        <Button
          type="button"
          className={`timer-btn start-stop-btn ${isRunning ? 'stop-state' : 'start-state'}`}
          onClick={isRunning ? stopTimer : startTimer}
        >
          {isRunning ? 'Stop' : 'Start'}
        </Button>
        
        <Button
          type="button"
          className="timer-btn reset-btn"
          onClick={resetTimer}
          disabled={isRunning}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

export default FastCatTimer;
